/**
 * ★ LEAVING WITH A CHANGE NOT YET SAVED ASKS FIRST (register R-018, lens finding C2-07).
 *
 * The autosave waits `AUTOSAVE_DEBOUNCE_MS` after the last keystroke, and a refused save stays on
 * this screen and nowhere else (./use-editor-draft.ts). Closing the tab, reloading, or following a
 * link inside the cabinet in that window lost the change without a word: nothing under `web/`
 * listened for `beforeunload` or blocked the router (grep of web/ui, web/authors and web/admin, 0).
 *
 * TWO DOORS, ONE CONDITION.
 *   - Leaving the PAGE (tab, reload, typed address): the browser's own `beforeunload` prompt. Its
 *     wording is the browser's; a page can only ask for it, by cancelling the event.
 *   - Moving INSIDE the cabinet (the sidebar, a link): the router's history is blocked and the
 *     author is asked with the browser's own confirm - one plain question, no dialog to build.
 *     Back and forward go through the same blocker. With no router around the screen (the unit
 *     specs mount it bare) only the first door exists.
 *
 * ★ REGISTERED IN THE EFFECT BODY, REMOVED IN ITS CLEANUP, KEYED ON THE CONDITION ALONE. StrictMode
 * (src/main.tsx) mounts, unmounts and re-mounts every component in development, and a listener
 * added once at creation and dropped by the first cleanup would be gone for the whole visit - the
 * liveness-guard defect ./use-editor-draft.ts records. And it must not nag: once the change is in,
 * the effect's cleanup takes both doors away again.
 */
import { useEffect } from 'react';
import { useRouter } from '@tanstack/react-router';
import type { AnyRouter } from '@tanstack/react-router';
import { articlesT } from '../articles-i18n';
import type { ArticlesT } from '../articles-i18n';
import type { SaveState } from './use-editor-draft';

/**
 * What the in-cabinet question says. The browser words the page-leaving one itself. Read when the
 * question is asked, so it is in the cabinet's language of that moment.
 */
export function leaveWarning(t: ArticlesT = articlesT()): string {
  return t('editor.leaveWarning');
}

/**
 * Is there work only this screen holds? Edited and waiting for the pause, in the air, or refused.
 * `clean` and `saved` are not: the platform has everything the author typed.
 */
export function hasUnsavedWork(saveState: SaveState): boolean {
  return saveState === 'pending' || saveState === 'saving' || saveState === 'failed';
}

export function useLeaveWarning(active: boolean): void {
  // `warn: false` - a screen mounted with no router (a unit spec) is a supported case here, and the
  // hook answers `undefined` there whatever its declared type says.
  const router = useRouter({ warn: false }) as AnyRouter | undefined;

  useEffect(() => {
    if (!active) {
      return undefined;
    }
    const onBeforeUnload = (event: BeforeUnloadEvent): void => {
      // Cancelling the event is what asks the browser to confirm; the text is its own.
      event.preventDefault();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    // The router's own mechanism for exactly this: a blocker that answers true STAYS on the page.
    const unblock = router?.history.block({
      blockerFn: () => !window.confirm(leaveWarning()),
      // The page-leaving door is the listener above, so the history does not add a second one.
      enableBeforeUnload: false,
    });
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      unblock?.();
    };
  }, [active, router]);
}
